import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, FacilityListResponse } from "../lib/api";
import { HelpButton } from "../components/HelpButton";
import { Search, Download } from "lucide-react";

export function FacilitiesPage() {
  const [q, setQ] = useState("");
  const [area, setArea] = useState("");
  const [careType, setCareType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ["facilities", q, area, careType, statusFilter, page],
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (area) params.set("area", area);
      if (careType) params.set("care_type", careType);
      if (statusFilter) params.set("status", statusFilter);
      params.set("limit", String(limit));
      params.set("offset", String(page * limit));
      return api.get<FacilityListResponse>(`/api/v1/facilities?${params}`);
    },
  });

  return (
    <div style={{ padding: "24px 32px" }}>
      <h1 style={{ fontSize: 24, color: "#1a365d", margin: "0 0 16px" }}>
        Лікарні {data && <span style={{ color: "#718096", fontSize: 16 }}>({data.total})</span>}
      </h1>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "1 1 250px", maxWidth: 360 }}>
          <Search size={14} style={{ position: "absolute", top: 10, left: 10, color: "#a0aec0" }} />
          <input
            placeholder="Пошук назви або ЄДРПОУ"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            style={{ ...inputStyle, paddingLeft: 30, width: "100%" }}
          />
        </div>
        <input placeholder="Область" value={area} onChange={(e) => { setArea(e.target.value); setPage(0); }} style={inputStyle} />
        <select value={careType} onChange={(e) => { setCareType(e.target.value); setPage(0); }} style={inputStyle}>
          <option value="">Профіль (всі)</option>
          <option value="Первинна">Первинна</option>
          <option value="Спеціалізована">Спеціалізована</option>
          <option value="Екстрена">Екстрена</option>
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} style={inputStyle}>
          <option value="">Статус (всі)</option>
          <option value="active">🟢 Діюча</option>
          <option value="war_zone">🔴 Зона бойових дій</option>
          <option value="relocated">🟠 Переміщена</option>
          <option value="new">🆕 Нова</option>
          <option value="closed">⚫ Закрита</option>
        </select>
        <a
          href={(() => {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (area) params.set("area", area);
            if (careType) params.set("care_type", careType);
            if (statusFilter) params.set("status", statusFilter);
            return `/api/v1/facilities/export.csv?${params}`;
          })()}
          target="_blank"
          rel="noreferrer"
          style={{
            padding: "7px 12px", background: "#38a169", color: "white",
            borderRadius: 4, textDecoration: "none", fontSize: 13,
            display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600,
          }}
          title="Завантажити CSV з поточними фільтрами"
        >
          <Download size={14} /> CSV
        </a>
      </div>

      {isLoading && <div style={{ color: "#718096" }}>Завантаження…</div>}

      {data && (
        <>
          <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
              <thead>
                <tr style={{ background: "#ebf4ff", color: "#2c5282", fontWeight: 600, textAlign: "left" }}>
                  <th style={th}>ЄДРПОУ</th>
                  <th style={th}>Назва <HelpButton anchor="facility-status" /></th>
                  <th style={th}>Область</th>
                  <th style={th}>Профіль</th>
                  <th style={th}>Роль у мережі <HelpButton anchor="peer-groups" /></th>
                  <th style={th}>Статус</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#a0aec0" }}>
                      Не знайдено. {data.total === 0 && "Синхронізація ще не запущена — подивіться /admin/sync."}
                    </td>
                  </tr>
                )}
                {data.items.map((f) => (
                  <tr key={f.edrpou} style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td style={td}>{f.edrpou}</td>
                    <td style={td}>
                      <Link to={`/facilities/${f.edrpou}`} style={{ color: "#2c5282", textDecoration: "none" }}>
                        {f.name}
                      </Link>
                    </td>
                    <td style={td}>{f.area || "—"}</td>
                    <td style={td}>{f.care_type || "—"}</td>
                    <td style={td}>{f.network_role || "—"}</td>
                    <td style={td}>{statusBadge(f.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, color: "#718096" }}>
            <div>Показано: {data.items.length} з {data.count} (всього в БД: {data.total})</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} style={pageBtn}>← Попередня</button>
              <span>Сторінка {page + 1}</span>
              <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= data.count} style={pageBtn}>Наступна →</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function statusBadge(status: string) {
  const colors: Record<string, string> = {
    active: "#48bb78",
    war_zone: "#e53e3e",
    relocated: "#ed8936",
    new: "#4299e1",
    closed: "#a0aec0",
  };
  const labels: Record<string, string> = {
    active: "Діюча",
    war_zone: "Зона бойових дій",
    relocated: "Переміщена",
    new: "Нова",
    closed: "Закрита",
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 12,
        background: (colors[status] || "#a0aec0") + "20",
        color: colors[status] || "#4a5568",
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {labels[status] || status}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "7px 10px",
  fontSize: 13,
  border: "1px solid #cbd5e0",
  borderRadius: 4,
  outline: "none",
};
const th: React.CSSProperties = { padding: "10px 12px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "8px 12px", verticalAlign: "middle" };
const pageBtn: React.CSSProperties = {
  padding: "4px 10px",
  fontSize: 12,
  background: "white",
  border: "1px solid #cbd5e0",
  borderRadius: 4,
  cursor: "pointer",
};
